/**
 * Events Engine (#48)
 * 
 * Manages multiple active events per club, scoping by dates, modes and participants,
 * custom ranking criteria and tie-breakers from eventCriteria catalogue, minMatches
 * qualification, and event winner resolution.
 */

import {
  isClassicSession,
  isRotationSession,
  isRankedSession,
} from './matchStats.js';
import {
  computePlayerStatsForEvent,
  getCriterion,
  formatStatValue,
} from './eventCriteria.js';

const toPlayerName = (p) => {
  if (!p) return '';
  if (typeof p === 'object') return p.name || p.id || p.uid || '';
  return String(p).trim();
};

/**
 * Normalizes an arbitrary date string or timestamp into a local calendar date string 'YYYY-MM-DD'.
 * Uses local date components (getFullYear/getMonth/getDate) so day boundaries honor the local day (#48).
 */
export function toDayKey(dateVal) {
  if (!dateVal) return null;

  const pad = (n) => String(n).padStart(2, '0');

  if (typeof dateVal === 'number') {
    const d = new Date(dateVal);
    if (Number.isNaN(d.getTime())) return null;
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  if (typeof dateVal === 'string') {
    const str = dateVal.trim();
    // If it's already a clean date string YYYY-MM-DD (without time), return it directly
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
      return str;
    }
    // Check DD/MM/YYYY (UK format)
    const ukMatch = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (ukMatch) {
      const day = ukMatch[1].padStart(2, '0');
      const month = ukMatch[2].padStart(2, '0');
      const year = ukMatch[3];
      return `${year}-${month}-${day}`;
    }
    // Parse ISO or timestamp string and format using local calendar parts
    const parsed = new Date(str);
    if (!Number.isNaN(parsed.getTime())) {
      return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
    }
  }

  return null;
}

/**
 * Extract all player names from a session.
 */
export function getSessionPlayers(session) {
  if (!session) return [];
  const players = new Set();

  if (Array.isArray(session.sessionPlayers)) {
    session.sessionPlayers.forEach((p) => {
      const name = toPlayerName(p);
      if (name) players.add(name);
    });
  }

  if (session.sessionStats && typeof session.sessionStats === 'object') {
    Object.keys(session.sessionStats).forEach((p) => {
      if (p) players.add(p);
    });
  }

  ['team1', 'team2', 'winningTeam', 'losingTeam'].forEach((k) => {
    const list = session[k] || session.matchSummary?.[k];
    if (Array.isArray(list)) {
      list.forEach((p) => {
        const name = toPlayerName(p);
        if (name) players.add(name);
      });
    }
  });

  return Array.from(players);
}

/**
 * Normalizes an event document with defaults for legacy seasons and new event options.
 */
export function normalizeEvent(eventDoc = {}) {
  const id = eventDoc.id || eventDoc.eventId || null;
  const name = eventDoc.name || 'Event';
  const description = eventDoc.description || '';

  // Start & End dates
  const startDate =
    toDayKey(eventDoc.startDate) ||
    toDayKey(eventDoc.startTimestamp) ||
    toDayKey(eventDoc.createdAt) ||
    '2020-01-01';

  const endDate =
    toDayKey(eventDoc.endDate) ||
    toDayKey(eventDoc.endTimestamp) ||
    null;

  // Status: if endDate is in the past, event is ended
  const isPast = Boolean(endDate && endDate < toDayKey(Date.now()));
  const status = eventDoc.status === 'ended' || isPast ? 'ended' : (eventDoc.status || 'active');

  // Modes: ['rotation', 'classic'] by default
  const modes = Array.isArray(eventDoc.modes) && eventDoc.modes.length > 0
    ? eventDoc.modes
    : ['rotation', 'classic'];

  // Participants: 'all' or array of names/ids
  const participants = Array.isArray(eventDoc.participants)
    ? eventDoc.participants.map(toPlayerName).filter(Boolean)
    : 'all';

  // Legacy explicit sessionIds membership (preserves legacy season standings)
  const sessionIds = Array.isArray(eventDoc.sessionIds)
    ? eventDoc.sessionIds
    : (Array.isArray(eventDoc.legacySessionIds) ? eventDoc.legacySessionIds : null);

  // Criterion: default to matchWins (desc)
  const rawCrit = eventDoc.criterion || {};
  const criterionStat = rawCrit.stat || (typeof eventDoc.criterion === 'string' ? eventDoc.criterion : 'matchWins');
  const criterion = {
    stat: criterionStat,
    direction: rawCrit.direction || getCriterion(criterionStat).direction || 'desc',
  };

  // Tie-breakers: up to 3 ordered tie-breakers. Defaults match today's standings: win % then gameDiff
  let tieBreakers = [];
  if (Array.isArray(eventDoc.tieBreakers)) {
    tieBreakers = eventDoc.tieBreakers.slice(0, 3).map((tb) => {
      const stat = typeof tb === 'string' ? tb : tb.stat;
      const direction = (typeof tb === 'object' && tb.direction) || getCriterion(stat).direction || 'desc';
      return { stat, direction };
    });
  } else {
    tieBreakers = [
      { stat: 'matchWinPct', direction: 'desc' },
      { stat: 'gameDiff', direction: 'desc' },
    ];
  }

  const minMatches = typeof eventDoc.minMatches === 'number' && eventDoc.minMatches > 0
    ? eventDoc.minMatches
    : 0;

  // Winner: frozen winner from ended event
  let winner = eventDoc.winner || null;
  if (!winner && eventDoc.eventMvp) {
    winner = {
      player: eventDoc.eventMvp,
      players: Array.isArray(eventDoc.tiedPlayers) && eventDoc.tiedPlayers.length > 0
        ? eventDoc.tiedPlayers
        : [eventDoc.eventMvp],
      decidedAt: eventDoc.endTimestamp || eventDoc.endedAt || null,
    };
  }

  return {
    ...eventDoc,
    id,
    name,
    description,
    startDate,
    endDate,
    status,
    modes,
    participants,
    sessionIds,
    legacySessionIds: sessionIds,
    criterion,
    tieBreakers,
    minMatches,
    winner,
    photoUrl: eventDoc.photoUrl || null,
    createdBy: eventDoc.createdBy || null,
    createdAt: eventDoc.createdAt || null,
  };
}

/**
 * Checks whether a session belongs to an event.
 * Validates:
 * 1. Ranked status (unranked sessions are excluded)
 * 2. Membership: if legacySessionIds / sessionIds is present, checks membership directly;
 *    otherwise checks local day boundaries (startDate to endDate).
 * 3. Mode (rotation vs classic)
 * 4. Participants (if participant list specified, targetPlayer or at least one participant must be present)
 */
export function isSessionInEvent(session, eventDoc, targetPlayer = null) {
  if (!session || !eventDoc) return false;

  // 1. Ranked check: unranked sessions are excluded
  if (!isRankedSession(session) || session.isUnranked === true) {
    return false;
  }

  const event = normalizeEvent(eventDoc);

  // 2. Session membership check:
  // If the event has an explicit sessionIds list (legacy seasons e.g. "Event 1"),
  // membership is governed by that list rather than dates (#48 review blocker fix).
  if (Array.isArray(event.sessionIds)) {
    if (!event.sessionIds.includes(session.id)) {
      return false;
    }
  } else {
    // Standard event: check local day boundaries
    const sessionDay = toDayKey(session.date || session.sessionDate || session.timestamp || session.createdAt);
    if (!sessionDay) return false;

    if (sessionDay < event.startDate) return false;
    if (event.endDate && sessionDay > event.endDate) return false;
  }

  // 3. Mode check
  const isClassic = isClassicSession(session);
  const isRotation = isRotationSession(session);
  if (isClassic && !event.modes.includes('classic')) return false;
  if (isRotation && !event.modes.includes('rotation')) return false;

  // 4. Participants check
  if (event.participants !== 'all' && Array.isArray(event.participants)) {
    const participantSet = new Set(event.participants.map((p) => p.toLowerCase().trim()));
    if (targetPlayer) {
      if (!participantSet.has(targetPlayer.toLowerCase().trim())) return false;
    } else {
      const sessionPlayers = getSessionPlayers(session);
      const hasParticipant = sessionPlayers.some((p) => participantSet.has(p.toLowerCase().trim()));
      if (!hasParticipant) return false;
    }
  }

  return true;
}

/**
 * Compute the complete standings for an event.
 */
export function getEventStandings(eventDoc, archivedSessions = [], allPlayers = []) {
  const event = normalizeEvent(eventDoc);

  // Filter eligible sessions for this event
  const eligibleSessions = archivedSessions.filter((s) => isSessionInEvent(s, event));

  // Determine candidate players
  const playerSet = new Set();

  if (event.participants !== 'all' && Array.isArray(event.participants)) {
    event.participants.forEach((p) => {
      if (p) playerSet.add(p);
    });
  } else {
    // All participants in eligible sessions
    eligibleSessions.forEach((s) => {
      getSessionPlayers(s).forEach((p) => playerSet.add(p));
    });
    // Add explicitly provided players
    allPlayers.forEach((p) => {
      const name = toPlayerName(p);
      if (name) playerSet.add(name);
    });
  }

  const critDef = getCriterion(event.criterion.stat);
  const critDir = event.criterion.direction || critDef.direction || 'desc';

  // Compute stats for each candidate player
  const candidateRows = [];

  playerSet.forEach((playerName) => {
    const rawStats = computePlayerStatsForEvent(eligibleSessions, playerName);

    // Only include players who have played or are in participants list
    const hasActivity = rawStats.mp > 0 || rawStats.gp > 0;
    const isExplicitParticipant = event.participants !== 'all' && Array.isArray(event.participants);
    if (!hasActivity && !isExplicitParticipant) {
      return;
    }

    const criterionValue = critDef.compute(rawStats);
    const formattedCriterion = formatStatValue(critDef.id, criterionValue);

    const tieBreakerValues = event.tieBreakers.map((tb) => {
      const tbDef = getCriterion(tb.stat);
      const val = tbDef.compute(rawStats);
      return {
        stat: tb.stat,
        direction: tb.direction || tbDef.direction || 'desc',
        value: val,
        formatted: formatStatValue(tbDef.id, val),
      };
    });

    const minMatches = event.minMatches || 0;
    const qualified = rawStats.mp >= minMatches;

    candidateRows.push({
      player: playerName,
      criterionValue,
      formattedCriterion,
      tieBreakerValues,
      stats: rawStats,
      qualified,
    });
  });

  // Sort rows
  candidateRows.sort((a, b) => {
    // Qualified players rank ahead of unqualified players
    if (a.qualified !== b.qualified) {
      return a.qualified ? -1 : 1;
    }

    // 1. Primary criterion
    if (b.criterionValue !== a.criterionValue) {
      return critDir === 'desc'
        ? b.criterionValue - a.criterionValue
        : a.criterionValue - b.criterionValue;
    }

    // 2. Tie-breakers in ordered sequence
    for (let i = 0; i < a.tieBreakerValues.length; i++) {
      const tbA = a.tieBreakerValues[i];
      const tbB = b.tieBreakerValues[i];
      if (tbA && tbB && tbB.value !== tbA.value) {
        return tbA.direction === 'desc'
          ? tbB.value - tbA.value
          : tbA.value - tbB.value;
      }
    }

    // 3. Alphabetical for deterministic display order
    return a.player.localeCompare(b.player);
  });

  // Assign standard sports ranks
  const standings = candidateRows.map((row, idx, list) => {
    if (!row.qualified) {
      return {
        ...row,
        rank: null,
        rankDisplay: 'Not qualified',
        isTied: false,
      };
    }

    // Check if tied with previous row across criterion and all tie-breakers
    let isTiedWithPrev = false;
    if (idx > 0 && list[idx - 1].qualified) {
      const prev = list[idx - 1];
      const sameCriterion = prev.criterionValue === row.criterionValue;
      const sameTieBreakers = prev.tieBreakerValues.every(
        (tb, i) => tb.value === row.tieBreakerValues[i]?.value
      );
      if (sameCriterion && sameTieBreakers) {
        isTiedWithPrev = true;
      }
    }

    const rank = isTiedWithPrev ? list[idx - 1].rank : idx + 1;
    row.rank = rank;

    // Check if next row ties with this row (for 'T' indicator)
    const isTiedWithNext = idx + 1 < list.length && list[idx + 1].qualified &&
      list[idx + 1].criterionValue === row.criterionValue &&
      row.tieBreakerValues.every((tb, i) => tb.value === list[idx + 1].tieBreakerValues[i]?.value);

    const isTied = isTiedWithPrev || isTiedWithNext;
    const rankDisplay = isTied ? `T${rank}` : String(rank);

    return {
      ...row,
      rank,
      rankDisplay,
      isTied,
    };
  });

  return standings;
}

/**
 * Determine the winner of an event.
 */
export function getEventWinner(eventDoc, standingsOrSessions, allPlayers = []) {
  if (!eventDoc) return null;
  const event = normalizeEvent(eventDoc);

  // If winner is already frozen on an ended event
  if (event.winner && event.winner.players && event.winner.players.length > 0) {
    return {
      player: event.winner.players.length === 1 ? event.winner.players[0] : 'Tie',
      players: event.winner.players,
      value: event.winner.value ?? null,
      criterion: event.winner.criterion || event.criterion.stat,
      decidedAt: event.winner.decidedAt || null,
      tied: event.winner.players.length > 1,
    };
  }

  // Calculate standings if sessions provided
  const standings = Array.isArray(standingsOrSessions) && standingsOrSessions.length > 0 && standingsOrSessions[0].rankDisplay !== undefined
    ? standingsOrSessions
    : getEventStandings(event, standingsOrSessions, allPlayers);

  // Find rank-1 qualified players
  const rank1Players = standings.filter((row) => row.rank === 1);
  if (rank1Players.length === 0) return null;

  const top = rank1Players[0];
  const isTie = rank1Players.length > 1;

  return {
    player: isTie ? 'Tie' : top.player,
    players: rank1Players.map((r) => r.player),
    value: top.criterionValue,
    formattedValue: top.formattedCriterion,
    criterion: event.criterion.stat,
    decidedAt: event.endDate ? `${event.endDate}T23:59:59.999Z` : new Date().toISOString(),
    tied: isTie,
  };
}

/**
 * Count total event wins across all ended events for Hall of Fame / Insights.
 * Counting ties for each tied player.
 */
export function countEventWins(events = []) {
  const counts = {};

  (events || []).forEach((ev) => {
    const event = normalizeEvent(ev);
    if (event.status !== 'ended') return;

    const winner = event.winner;
    if (winner) {
      const winningPlayers = Array.isArray(winner.players) && winner.players.length > 0
        ? winner.players
        : (winner.player && winner.player !== 'Tie' ? [winner.player] : []);

      winningPlayers.forEach((p) => {
        if (!p || p === 'Tie') return;
        counts[p] = (counts[p] || 0) + 1;
      });
    }
  });

  return counts;
}

/**
 * Migrates a legacy season document to the new event model.
 * Preserves legacy sessionIds list so existing membership is unchanged (#48).
 */
export function migrateLegacyEvent(legacyDoc = {}) {
  const normalized = normalizeEvent(legacyDoc);
  const sessionIds = Array.isArray(legacyDoc.sessionIds) ? legacyDoc.sessionIds : normalized.sessionIds;

  return {
    ...normalized,
    sessionIds,
    legacySessionIds: sessionIds,
    criterion: { stat: 'matchWins', direction: 'desc' },
    tieBreakers: [
      { stat: 'matchWinPct', direction: 'desc' },
      { stat: 'gameDiff', direction: 'desc' },
    ],
    modes: ['rotation', 'classic'],
    participants: 'all',
  };
}
